import React, { useEffect, useState } from 'react';
import DateCalendar from '../DateCalendar';
import { ETL_RUNS_PAGE_SIZE } from '../../api/client';
import type { ETLRun } from './types';

export interface ETLRunsSectionProps {
  searchRunName: string;
  setSearchRunName: (v: string) => void;
  searchStartDate: string;
  setSearchStartDate: (v: string) => void;
  searchEndDate: string;
  setSearchEndDate: (v: string) => void;
  loadETLRuns: () => void;
  loadingRuns: boolean;
  runsError: string;
  selectedDate: string | null;
  setSelectedDate: (v: string | null) => void;
  currentMonth: string;
  setCurrentMonth: (v: string) => void;
  datesWithRunStarts: string[];
  datesWithData: string[];
  filteredETLRuns: ETLRun[];
  onRunClick: (run: ETLRun) => void;
}

const ETLRunsSection: React.FC<ETLRunsSectionProps> = ({
  searchRunName,
  setSearchRunName,
  searchStartDate,
  setSearchStartDate,
  searchEndDate,
  setSearchEndDate,
  loadETLRuns,
  loadingRuns,
  runsError,
  selectedDate,
  setSelectedDate,
  currentMonth,
  setCurrentMonth,
  datesWithRunStarts,
  datesWithData,
  filteredETLRuns,
  onRunClick,
}) => {
  const [listPage, setListPage] = useState(0);

  useEffect(() => {
    setListPage(0);
  }, [selectedDate, currentMonth, searchRunName, searchStartDate, searchEndDate, filteredETLRuns.length]);

  const pageStart = listPage * ETL_RUNS_PAGE_SIZE;
  const pageRuns = filteredETLRuns.slice(pageStart, pageStart + ETL_RUNS_PAGE_SIZE);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2>ETL Runs</h2>
      <div className="plot-controls">
        <div className="control-group">
          <label>Search Run Name:</label>
          <input
            type="text"
            value={searchRunName}
            onChange={(e) => setSearchRunName(e.target.value)}
            placeholder="Filter by run name..."
            style={{ padding: '0.5rem' }}
          />
        </div>
        <div className="control-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={searchStartDate}
            onChange={(e) => setSearchStartDate(e.target.value)}
            style={{ padding: '0.5rem' }}
          />
        </div>
        <div className="control-group">
          <label>End Date:</label>
          <input
            type="date"
            value={searchEndDate}
            onChange={(e) => setSearchEndDate(e.target.value)}
            style={{ padding: '0.5rem' }}
          />
        </div>
        <div className="control-group">
          <button
            onClick={loadETLRuns}
            disabled={loadingRuns}
            style={{ padding: '0.5rem 1rem', marginTop: '1rem' }}
          >
            {loadingRuns ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {runsError && <div className="error">Error: {runsError}</div>}

      <div style={{ marginTop: '1rem' }}>
        <DateCalendar
          selectedDate={selectedDate}
          datesWithRunStarts={datesWithRunStarts}
          datesWithData={datesWithData}
          currentMonth={currentMonth}
          onDateSelect={(date) => {
            if (selectedDate === date) {
              setSelectedDate(null);
            } else {
              setSelectedDate(date);
              const dateObj = new Date(date + 'T00:00:00');
              const monthStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
              if (monthStr !== currentMonth) {
                setCurrentMonth(monthStr);
                const year = dateObj.getFullYear();
                const month = dateObj.getMonth() + 1;
                const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
                setSearchStartDate(firstDay);
                setSearchEndDate(lastDay);
              }
            }
          }}
          onMonthChange={(monthStr) => {
            setSelectedDate(null);
            setCurrentMonth(monthStr);
            const [year, month] = monthStr.split('-').map(Number);
            const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
            const lastDay = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
            setSearchStartDate(firstDay);
            setSearchEndDate(lastDay);
          }}
        />
      </div>

      <div style={{ marginTop: '1rem', maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        {filteredETLRuns.length === 0 && !loadingRuns && (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
            {selectedDate
              ? `No ETL runs found for ${selectedDate}`
              : `No ETL runs found for ${currentMonth}`}
          </div>
        )}
        {pageRuns.map((run, idx) => (
          <div
            key={run.id ?? `${run.run_name}-${pageStart + idx}`}
            onClick={() => onRunClick(run)}
            style={{
              padding: '1rem',
              borderBottom: '1px solid #dee2e6',
              cursor: 'pointer',
              background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e7f3ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8f9fa';
            }}
          >
            <div style={{ fontWeight: 'bold' }}>{run.run_name}</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              Robot: {run.robot_name} | Status: {run.status}
              {run.cleaning_status ? ` | Cleaning: ${run.cleaning_status}` : ''}
              {run.needs_attention ? ' | needs attention' : ''}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#999' }}>
              Start: {run.start_time ? run.start_time.replace('T', ' ').replace('.000Z', ' UTC') : 'unknown'}
              {run.end_time && ` | End: ${run.end_time.replace('T', ' ').replace('.000Z', ' UTC')}`}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>
          {filteredETLRuns.length === 0
            ? 'No runs'
            : `${pageStart + 1}–${pageStart + pageRuns.length} of ${filteredETLRuns.length}`}
        </span>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button
            disabled={listPage === 0}
            onClick={() => setListPage(listPage - 1)}
            style={{ padding: '0.5rem 1rem' }}
          >
            Previous
          </button>
          <button
            disabled={pageStart + ETL_RUNS_PAGE_SIZE >= filteredETLRuns.length}
            onClick={() => setListPage(listPage + 1)}
            style={{ padding: '0.5rem 1rem' }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ETLRunsSection;
